import React from 'react';
import type { Business } from '../../../types/business';

interface BusinessConsultantHubHeaderProps {
  chatsCount: number;
  businesses: Business[];
  selectedBusinessId: string | null;
  onBusinessChange: (businessId: string | null) => void;
  onNewChat: () => void;
}

const BusinessConsultantHubHeader: React.FC<BusinessConsultantHubHeaderProps> = ({ 
  chatsCount, 
  businesses, 
  selectedBusinessId, 
  onBusinessChange, 
  onNewChat 
}) => {
  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          {/* Consultant Avatar */}
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-2xl font-bold text-white shadow-lg">
            AI
          </div>
          
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Business Consultant</h1>
            <p className="mt-1 text-sm text-gray-600">
              Your intelligent business advisor available 24/7
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Stats */}
          <div className="hidden sm:flex items-center gap-4 mr-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{chatsCount}</div>
              <div className="text-xs text-gray-500">Conversations</div>
            </div>
            <div className="h-10 w-px bg-gray-200"></div>
            <div className="text-center">
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700">Online</span>
              </div>
              <div className="text-xs text-gray-500">Ready to help</div>
            </div>
          </div>

          {/* Business Context Selector */}
          {businesses.length > 0 && (
            <select
              value={selectedBusinessId || ''}
              onChange={(e) => onBusinessChange(e.target.value || null)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <option value="">No business context</option>
              {businesses.map((business) => (
                <option key={business.id} value={business.id}>
                  {business.name}
                </option>
              ))}
            </select>
          )}

          {/* New Chat Button */}
          <button
            onClick={onNewChat}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>New Chat</span>
          </button>
        </div>
      </div>

      {/* Quick Info Bar */}
      <div className="mt-4 grid grid-cols-3 gap-4 border-t border-gray-100 pt-4">
        <div className="text-center">
          <div className="text-sm font-medium text-gray-500">Expertise</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            Strategy • Marketing • Finance
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm font-medium text-gray-500">Response Time</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            Instant
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm font-medium text-gray-500">Context</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            CRM • Plans • Ideas
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessConsultantHubHeader;
